import { BanIcon, CheckIcon, ChevronRightIcon, DownloadIcon, FileSpreadsheetIcon, Layers3Icon, MoreHorizontalIcon, PanelRightOpenIcon, PencilIcon, PlusIcon, SearchIcon, SlidersHorizontalIcon, Trash2Icon, UploadIcon, UsersIcon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { FigureBand } from '@/components/FigureBand';
import { ErrorState } from '@/components/ErrorState';
import { TableSkeleton } from '@/components/LoadingBlock';
import { Pagination } from '@/components/Pagination';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RailOutletSlot } from '@/components/RailOutletSlot';
import { ContactSourceBadge } from '@/components/StatusBadge';
import { useTenantSettings } from '@/features/org/queries';
import { useJobContext } from '@/layouts/JobLayout';
import { api, errorMessage } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { fmtDate, fmtNumber } from '@/lib/format';
import { useDebounce } from '@/lib/hooks';
import type { ContactDto, ContactListParams, ContactSource } from '@/lib/types';
import { withParam } from '@/lib/url';
import { cn, initials } from '@/lib/utils';
import { ContactRail, type RailKey } from './ContactRail';
import { ImportDialog } from './ImportDialog';
import { useContactStats, useContacts, useDeleteContact, useTags, useUpdateContact } from './queries';

const ALL = '__all__';
type Sort = 'name' | 'created' | 'phone';
type Dir = 'asc' | 'desc';
const SORT_LABEL: Record<`${Sort}:${Dir}`, string> = { 'name:asc': 'Name A–Z', 'name:desc': 'Name Z–A', 'created:desc': 'Newest first', 'created:asc': 'Oldest first', 'phone:asc': 'Phone number', 'phone:desc': 'Phone number (desc)' };

const PAGE_SIZES = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_KEY = 'crewline:contacts-page-size';

function rememberPageSize(size: number): void {
  try {
    localStorage.setItem(PAGE_SIZE_KEY, String(size));
  } catch {
    /* ignore */
  }
}

/** Rows per page: `?size=` wins, then the remembered choice, then 25. */
function pageSizeFromParam(raw: string | null): number {
  const fromUrl = Number(raw);
  if (PAGE_SIZES.includes(fromUrl)) return fromUrl;
  try {
    const stored = Number(localStorage.getItem(PAGE_SIZE_KEY));
    if (PAGE_SIZES.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  return DEFAULT_PAGE_SIZE;
}

/**
 * A job's contact list. One toolbar row: search, a Filters popover (tag, language, source, status)
 * whose active choices show as removable chips, sort, then Add contact and a menu for import /
 * export. Clicking a name opens the contact in the app's right rail (pinned beside the page on wide
 * screens, a drawer on phones), where every open contact is a tab and a ticked selection gets a
 * Bulk edit tab. Deep link: /jobs/:jobId/contacts/:contactId opens that contact.
 */
export function ContactsPage() {
  const { job } = useJobContext();
  const me = useMe();
  const settings = useTenantSettings();
  const [params, setParams] = useSearchParams();

  // ---- list state --------------------------------------------------------------------------
  const [q, setQ] = useState('');
  const debouncedQ = useDebounce(q, 300);
  const [tag, setTag] = useState<string>(ALL);
  const [language, setLanguage] = useState<string>(ALL);
  const [source, setSource] = useState<string>(ALL);
  const [optedOut, setOptedOut] = useState<string>(ALL);
  const [sort, setSort] = useState<Sort>('name');
  const [dir, setDir] = useState<Dir>('asc');
  // Page and rows per page live in the URL, so a page survives opening a contact and can be linked; the size is remembered too.
  const page = Math.max(1, Number(params.get('page')) || 1);
  const pageSize = pageSizeFromParam(params.get('size'));
  const setPage = (p: number) => setParams((prev) => withParam(prev, 'page', p > 1 ? String(p) : null), { replace: true });
  const setPageSize = (s: number) => {
    rememberPageSize(s);
    setParams((prev) => withParam(withParam(prev, 'size', s === DEFAULT_PAGE_SIZE ? null : String(s)), 'page', null), { replace: true });
  };
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Last row whose checkbox was clicked, so shift-click can select the range up to the next one. */
  const anchorRef = useRef<number | null>(null);
  const shiftRef = useRef(false);

  // ---- rail / sheet state -------------------------------------------------------------------
  const [openTabs, setOpenTabs] = useState<ContactDto[]>([]);
  const [newOpen, setNewOpen] = useState(false);
  const [active, setActive] = useState<RailKey | null>(null);
  /** Bulk edit was asked for explicitly (so ticking rows on a phone never pops the drawer by itself). */
  const [bulkOpen, setBulkOpen] = useState(false);
  const { contactId } = useParams<{ contactId?: string }>();
  const navigate = useNavigate();

  const importOpen = params.get('import') === '1';
  const [deleting, setDeleting] = useState<ContactDto | null>(null);
  const [optToggling, setOptToggling] = useState<ContactDto | null>(null);

  const listParams: ContactListParams = useMemo(
    () => ({
      page,
      pageSize,
      q: debouncedQ || undefined,
      tag: tag === ALL ? undefined : tag,
      language: language === ALL ? undefined : language,
      source: source === ALL ? undefined : (source as ContactSource),
      optedOut: optedOut === ALL ? undefined : (optedOut as 'true' | 'false'),
      sort,
      dir,
    }),
    [page, pageSize, debouncedQ, tag, language, source, optedOut, sort, dir],
  );
  const contacts = useContacts(job.id, listParams);
  const tags = useTags(job.id);
  const stats = useContactStats(job.id);
  const update = useUpdateContact(job.id);
  const del = useDeleteContact(job.id);

  const items = contacts.data?.items ?? [];
  // Search, filters and sort start over from page 1 (the first render keeps a linked page).
  const filterSig = JSON.stringify([debouncedQ, tag, language, source, optedOut, sort, dir]);
  const lastFilterSig = useRef(filterSig);
  useEffect(() => {
    if (lastFilterSig.current === filterSig) return;
    lastFilterSig.current = filterSig;
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSig]);
  // Keep the selection across refetches (bulk edits refetch), but drop rows that left the page.
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => items.some((c) => c.id === id)));
      return next.size === prev.size ? prev : next;
    });
    // Refresh open tabs with the latest copy of each contact.
    setOpenTabs((prev) => prev.map((t) => items.find((c) => c.id === t.id) ?? t));
  }, [items]);

  // ---- rail helpers -----------------------------------------------------------------------
  const openContact = useCallback((c: ContactDto) => {
    setOpenTabs((prev) => (prev.some((t) => t.id === c.id) ? prev : [...prev, c]));
    setActive(c.id);
  }, []);
  const openNew = () => {
    setNewOpen(true);
    setActive('new');
  };
  const closeTab = (key: RailKey) => {
    if (key === 'new') setNewOpen(false);
    else if (key !== 'bulk') setOpenTabs((prev) => prev.filter((t) => t.id !== key));
    if (contactId && key === contactId) navigate(`/jobs/${job.id}/contacts`, { replace: true });
    setActive((cur) => (cur === key ? null : cur));
  };
  const closeAll = () => {
    setOpenTabs([]);
    setNewOpen(false);
    setBulkOpen(false);
    setActive(null);
    if (contactId) navigate(`/jobs/${job.id}/contacts`, { replace: true });
  };
  const onSaved = (fromKey: RailKey, saved?: ContactDto) => {
    if (fromKey === 'new') {
      setNewOpen(false);
      if (saved) openContact(saved);
      else setActive(null);
    } else if (saved) {
      setOpenTabs((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
    }
  };

  // Deep link: open that contact, fetching it if it is not on this page.
  useEffect(() => {
    if (!contactId) return;
    const onPage = items.find((c) => c.id === contactId);
    if (onPage) {
      openContact(onPage);
      return;
    }
    let cancelled = false;
    void api.contacts
      .get(job.id, contactId)
      .then(({ contact }) => {
        if (!cancelled) openContact(contact);
      })
      .catch((err: unknown) => {
        if (!cancelled) toast.error(errorMessage(err, 'That contact could not be found'));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, job.id]);

  const setImportOpen = (open: boolean) => {
    const next = new URLSearchParams(params);
    if (open) next.set('import', '1');
    else next.delete('import');
    setParams(next, { replace: true });
  };

  const allSelected = items.length > 0 && items.every((c) => selected.has(c.id));
  const someSelected = items.some((c) => selected.has(c.id));
  const selectedContacts = items.filter((c) => selected.has(c.id));
  const showLanguage = me.tenant.features.translation || me.tenant.i18n.enabledLanguages.length > 1;
  const langName = (code: string) => settings.data?.languages.find((l) => l.code === code)?.name ?? code.toUpperCase();

  const filters: Array<{ key: string; label: string; clear: () => void }> = [
    ...(tag !== ALL ? [{ key: 'tag', label: `Tag: ${tag}`, clear: () => setTag(ALL) }] : []),
    ...(language !== ALL ? [{ key: 'language', label: `Language: ${langName(language)}`, clear: () => setLanguage(ALL) }] : []),
    ...(source !== ALL ? [{ key: 'source', label: `Source: ${source === 'web_form' ? 'Web sign-up' : source === 'self_service' ? 'Joined by text' : source === 'import' ? 'Imported' : 'Added manually'}`, clear: () => setSource(ALL) }] : []),
    ...(optedOut !== ALL ? [{ key: 'status', label: optedOut === 'true' ? 'Opted out' : 'Subscribed', clear: () => setOptedOut(ALL) }] : []),
  ];
  const clearFilters = () => {
    setTag(ALL);
    setLanguage(ALL);
    setSource(ALL);
    setOptedOut(ALL);
  };
  const hasFilters = !!debouncedQ || filters.length > 0;

  const toggleAll = (checked: boolean) => {
    anchorRef.current = null;
    setSelected((prev) => {
      const next = new Set(prev);
      for (const c of items) checked ? next.add(c.id) : next.delete(c.id);
      return next;
    });
  };
  /** Tick or untick one row; with Shift held, every row between the last clicked row and this one. */
  const toggleRow = (index: number, checked: boolean, shift: boolean) => {
    const from = shift && anchorRef.current !== null ? Math.min(anchorRef.current, index) : index;
    const to = shift && anchorRef.current !== null ? Math.max(anchorRef.current, index) : index;
    setSelected((prev) => {
      const next = new Set(prev);
      for (let i = from; i <= to; i++) {
        const id = items[i]?.id;
        if (id) checked ? next.add(id) : next.delete(id);
      }
      return next;
    });
    anchorRef.current = index;
  };

  const toggleOptOut = async (c: ContactDto) => {
    try {
      await update.mutateAsync({ contactId: c.id, body: { optedOut: !c.optedOut } });
      toast.success(c.optedOut ? `${c.fullName} can receive messages again` : `${c.fullName} opted out`);
      setOptToggling(null);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const showBulk = bulkOpen && selectedContacts.length >= 2;
  const railVisible = openTabs.length > 0 || newOpen || showBulk;
  const deselect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  return (
    <>
      <RailOutletSlot
        visible={railVisible}
        onClose={closeAll}
        label="Contact details"
        content={
          <ContactRail
            jobId={job.id}
            open={openTabs}
            newOpen={newOpen}
            active={active}
            selected={showBulk ? selectedContacts : []}
            onActivate={setActive}
            onClose={closeTab}
            onCloseAll={closeAll}
            onSaved={onSaved}
            onDeselect={deselect}
            onClearSelection={() => {
              setSelected(new Set());
              setBulkOpen(false);
            }}
          />
        }
      />
      <div className="min-w-0 space-y-4">
        {/* The list at a glance: how many, how many can be reached, who opted out, what they speak. */}
        <FigureBand
          ariaLabel="Contacts at a glance"
          figures={[
            { label: 'Contacts', value: stats.data ? fmtNumber(stats.data.total) : undefined, hint: tags.data ? `${fmtNumber(tags.data.items.length)} ${tags.data.items.length === 1 ? 'tag' : 'tags'} in use` : undefined },
            { label: 'Reachable', value: stats.data ? fmtNumber(stats.data.total - stats.data.optedOut) : undefined, hint: 'Subscribed, get every send' },
            { label: 'Opted out', value: stats.data ? fmtNumber(stats.data.optedOut) : undefined, bad: (stats.data?.optedOut ?? 0) > 0, hint: 'Skipped automatically' },
            {
              label: 'Languages',
              value: stats.data ? fmtNumber(stats.data.languages.length) : undefined,
              hint: stats.data
                ? [...stats.data.languages]
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 3)
                    .map((l) => `${langName(l.language)} ${fmtNumber(l.count)}`)
                    .join(' · ')
                : undefined,
            },
          ]}
        />

        {/* Toolbar */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1 sm:max-w-sm">
              <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, company…" className="pl-8" aria-label="Search contacts" />
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" aria-label="Filters">
                  <SlidersHorizontalIcon /> Filters
                  {filters.length > 0 && <span className="bg-primary text-primary-foreground grid size-5 place-items-center text-[11px] font-semibold tabular-nums">{filters.length}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80">
                <div className="grid gap-4">
                  <FilterRow label="Tag">
                    <Select value={tag} onValueChange={setTag}>
                      <SelectTrigger className="w-full" aria-label="Filter by tag">
                        <SelectValue placeholder="All tags" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL}>All tags</SelectItem>
                        {(tags.data?.items ?? []).map((t) => (
                          <SelectItem key={t.tag} value={t.tag}>
                            {t.tag} ({t.count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FilterRow>
                  {showLanguage && (
                    <FilterRow label="Language">
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger className="w-full" aria-label="Filter by language">
                          <SelectValue placeholder="All languages" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL}>All languages</SelectItem>
                          {me.tenant.i18n.enabledLanguages.map((code) => (
                            <SelectItem key={code} value={code}>
                              {langName(code)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FilterRow>
                  )}
                  <FilterRow label="Source">
                    <Select value={source} onValueChange={setSource}>
                      <SelectTrigger className="w-full" aria-label="Filter by source">
                        <SelectValue placeholder="Any source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL}>Any source</SelectItem>
                        <SelectItem value="self_service">Joined by text</SelectItem>
                        <SelectItem value="web_form">Web sign-up</SelectItem>
                        <SelectItem value="import">Imported</SelectItem>
                        <SelectItem value="manual">Added manually</SelectItem>
                      </SelectContent>
                    </Select>
                  </FilterRow>
                  <FilterRow label="Status">
                    <Select value={optedOut} onValueChange={setOptedOut}>
                      <SelectTrigger className="w-full" aria-label="Filter by opt-out status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL}>Any status</SelectItem>
                        <SelectItem value="false">Subscribed</SelectItem>
                        <SelectItem value="true">Opted out</SelectItem>
                      </SelectContent>
                    </Select>
                  </FilterRow>
                  <div className="flex items-center justify-between border-t pt-3">
                    <span className="text-muted-foreground text-xs">{filters.length === 0 ? 'No filters applied' : `${filters.length} applied`}</span>
                    <Button variant="ghost" size="sm" onClick={clearFilters} disabled={filters.length === 0}>
                      Clear filters
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <Select
              value={`${sort}:${dir}`}
              onValueChange={(v) => {
                const [s, d] = v.split(':') as [Sort, Dir];
                setSort(s);
                setDir(d);
              }}
            >
              <SelectTrigger className="w-[150px]" aria-label="Sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['name:asc', 'name:desc', 'created:desc', 'created:asc', 'phone:asc'] as const).map((k) => (
                  <SelectItem key={k} value={k}>
                    {SORT_LABEL[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="ml-auto flex items-center gap-2">
              <Button onClick={openNew}>
                <PlusIcon /> Add contact
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Import and export">
                    <MoreHorizontalIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel>Spreadsheets</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setImportOpen(true)}>
                    <UploadIcon /> Import from a file
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => void api.contacts.exportCsv(job.id).catch((e: unknown) => toast.error(errorMessage(e)))}>
                    <DownloadIcon /> Export CSV
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => void api.contacts.templateXlsx(job.id).catch((e: unknown) => toast.error(errorMessage(e)))}>
                    <FileSpreadsheetIcon /> Excel template
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => void api.contacts.template(job.id).catch((e: unknown) => toast.error(errorMessage(e)))}>
                    <FileSpreadsheetIcon /> CSV template
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {(tags.data?.items.length ?? 0) > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by tag">
              <span className="text-muted-foreground mr-0.5 text-[11px] font-medium tracking-wide uppercase">Tags</span>
              {(tags.data?.items ?? []).slice(0, 12).map((t) => {
                const on = tag === t.tag;
                return (
                  <button key={t.tag} type="button" aria-pressed={on} onClick={() => setTag(on ? ALL : t.tag)} className={cn('flex h-7 items-center gap-1.5 border px-2 text-xs transition-colors', on ? 'border-primary bg-primary text-primary-foreground' : 'bg-card text-foreground hover:border-foreground/30')}>
                    {on && <CheckIcon className="size-3" aria-hidden />}
                    {t.tag}
                    <span className={cn('tabular-nums', on ? 'opacity-80' : 'text-muted-foreground')}>{fmtNumber(t.count)}</span>
                  </button>
                );
              })}
              {(tags.data?.items.length ?? 0) > 12 && <span className="text-muted-foreground text-xs">+{(tags.data?.items.length ?? 0) - 12} more in Filters</span>}
            </div>
          )}

          {filters.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {filters.map((f) => (
                <Badge key={f.key} variant="secondary" className="gap-1 pr-1">
                  {f.label}
                  <button type="button" onClick={f.clear} aria-label={`Clear ${f.label}`} className="hover:bg-foreground/10 grid size-4 place-items-center">
                    <XIcon className="size-3" />
                  </button>
                </Badge>
              ))}
              <button type="button" onClick={clearFilters} className="text-muted-foreground hover:text-foreground ml-1 underline-offset-4 hover:underline">
                Clear all
              </button>
            </div>
          )}
        </div>

        {contacts.data && contacts.data.total > pageSize && <Pagination compact page={page} pageSize={pageSize} total={contacts.data.total} onPageChange={setPage} />}

        {/* Selection bar */}
        {someSelected && (
          <div className="bg-muted/50 flex flex-wrap items-center gap-2 border px-3 py-2 text-sm">
            <span className="font-medium tabular-nums">{selected.size} selected</span>
            <span className="text-muted-foreground">·</span>
            {selected.size >= 2 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBulkOpen(true);
                  setActive('bulk');
                }}
              >
                <Layers3Icon /> Bulk edit
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => selectedContacts[0] && openContact(selectedContacts[0])}>
                <PencilIcon /> Open
              </Button>
            )}
            {selected.size >= 2 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const toOpen = selectedContacts.slice(0, 8);
                  setOpenTabs((prev) => [...prev, ...toOpen.filter((c) => !prev.some((t) => t.id === c.id))]);
                  if (toOpen[0]) setActive(toOpen[0].id);
                }}
                title="Open each selected contact as a tab in the panel"
              >
                <PanelRightOpenIcon /> Open {selectedContacts.length > 8 ? 'first 8' : 'selected'}
              </Button>
            )}
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        )}

        {/* Table */}
        {contacts.isPending ? (
          <TableSkeleton rows={8} cols={6} />
        ) : contacts.isError ? (
          <ErrorState error={contacts.error} onRetry={() => void contacts.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title={hasFilters ? 'No contacts match' : 'No contacts yet'}
            description={hasFilters ? 'Try a different search or clear the filters.' : 'Import an Excel workbook or CSV, let workers text JOIN at the gate, or add contacts one at a time.'}
            action={
              hasFilters ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setQ('');
                    clearFilters();
                  }}
                >
                  Clear filters
                </Button>
              ) : (
                <div className="flex flex-wrap justify-center gap-2">
                  <Button onClick={() => setImportOpen(true)}>
                    <UploadIcon /> Import a spreadsheet
                  </Button>
                  <Button variant="outline" onClick={openNew}>
                    <PlusIcon /> Add one contact
                  </Button>
                </div>
              )
            }
          />
        ) : (
          <>
          <ul className="bg-card divide-y border md:hidden" aria-label="Contacts">
            {items.map((c) => {
              const isOpen = openTabs.some((t) => t.id === c.id);
              return (
                <li key={c.id}>
                  <button type="button" onClick={() => openContact(c)} className={cn('active:bg-accent/40 flex w-full items-center gap-3 px-4 py-3 text-left', isOpen && active === c.id && 'bg-accent/40')}>
                    <Avatar className="size-9 shrink-0">
                      <AvatarFallback className={cn('text-xs', c.optedOut && 'text-muted-foreground/60')}>{initials(c.fullName || c.phoneDisplay)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium">{c.fullName || c.phoneDisplay}</span>
                        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{c.phoneDisplay}</span>
                      </div>
                      <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 truncate text-xs">
                        <span className={cn('size-1.5 shrink-0 rounded-full', c.optedOut ? 'bg-warning' : 'bg-success')} aria-hidden />
                        <span className="truncate">{[c.optedOut ? 'Opted out' : 'Subscribed', c.company, c.title].filter(Boolean).join(' · ')}</span>
                      </div>
                      {c.tags.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {c.tags.map((t) => (
                            <span key={t} className="bg-muted text-muted-foreground px-1.5 py-0.5 text-[11px]">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <ChevronRightIcon className="text-muted-foreground/50 size-4 shrink-0" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="bg-card hidden overflow-x-auto border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={allSelected ? true : someSelected ? 'indeterminate' : false} onCheckedChange={(v) => toggleAll(Boolean(v))} aria-label="Select all on this page" />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Company / title</TableHead>
                  {showLanguage && <TableHead>Language</TableHead>}
                  <TableHead>Tags</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c, index) => {
                  const isOpen = openTabs.some((t) => t.id === c.id);
                  return (
                    <TableRow key={c.id} data-state={selected.has(c.id) ? 'selected' : undefined} className={cn(isOpen && active === c.id && 'bg-accent/40')}>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(c.id)}
                          onPointerDown={(e) => {
                            shiftRef.current = e.shiftKey;
                            if (e.shiftKey) e.preventDefault();
                          }}
                          onClick={(e) => {
                            shiftRef.current = e.shiftKey;
                          }}
                          onCheckedChange={(v) => toggleRow(index, Boolean(v), shiftRef.current)}
                          aria-label={`Select ${c.fullName}`}
                          title="Shift-click to select a range"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex min-w-0 items-center gap-3">
                          <Avatar className="size-8 shrink-0">
                            <AvatarFallback className={cn('text-[11px]', c.optedOut && 'text-muted-foreground/60')}>{initials(c.fullName || c.phoneDisplay)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <button type="button" className={cn('block max-w-full truncate text-left font-medium hover:underline', isOpen && 'underline decoration-dotted underline-offset-4')} onClick={() => openContact(c)}>
                              {c.fullName || c.phoneDisplay}
                            </button>
                            <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                              {c.email && <span className="truncate">{c.email}</span>}
                              {(c.source === 'self_service' || c.source === 'web_form') && <ContactSourceBadge source={c.source} />}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="tabular-nums">{c.phoneDisplay}</TableCell>
                      <TableCell>
                        <div>{c.company ?? <span className="text-muted-foreground">—</span>}</div>
                        {c.title && <div className="text-muted-foreground text-xs">{c.title}</div>}
                      </TableCell>
                      {showLanguage && (
                        <TableCell>
                          <button type="button" className="text-muted-foreground hover:text-foreground text-xs" onClick={() => setLanguage(c.language)} title="Filter by this language">
                            {langName(c.language)}
                          </button>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex max-w-[240px] flex-wrap gap-1">
                          {c.tags.map((t) => (
                            <Badge key={t} variant="secondary" className="cursor-pointer" onClick={() => setTag(t)}>
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusDot optedOut={c.optedOut} consent={c.consent ? `${c.consent.method}${c.consent.keyword ? ` (${c.consent.keyword})` : ''}` : null} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(c.createdAt)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${c.fullName}`}>
                              <MoreHorizontalIcon />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openContact(c)}>
                              <PencilIcon /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setOptToggling(c)}>
                              <BanIcon /> {c.optedOut ? 'Mark as subscribed' : 'Mark as opted out'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem variant="destructive" onClick={() => setDeleting(c)}>
                              <Trash2Icon /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          </>
        )}

        {contacts.data && contacts.data.total > pageSize && <Pagination page={page} pageSize={pageSize} total={contacts.data.total} onPageChange={setPage} onPageSizeChange={setPageSize} pageSizes={PAGE_SIZES} />}
      </div>

      <ImportDialog jobId={job.id} open={importOpen} onOpenChange={setImportOpen} />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete ${deleting?.fullName}?`}
        description="The contact is removed from this job. Message history that already references them is kept."
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await del.mutateAsync(deleting.id);
            toast.success('Contact deleted');
            closeTab(deleting.id);
            setDeleting(null);
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      />

      <ConfirmDialog
        open={!!optToggling}
        onOpenChange={(o) => !o && setOptToggling(null)}
        title={optToggling?.optedOut ? `Re-subscribe ${optToggling?.fullName}?` : `Opt out ${optToggling?.fullName}?`}
        description={
          optToggling?.optedOut
            ? 'Only do this if the person asked to receive messages again (for example, they texted START). Re-subscribing someone who replied STOP violates carrier rules.'
            : 'They will be skipped on every send from this job until re-subscribed. Use this when someone asks to stop receiving texts through any channel.'
        }
        confirmLabel={optToggling?.optedOut ? 'Re-subscribe' : 'Opt out'}
        destructive={!optToggling?.optedOut}
        loading={update.isPending}
        onConfirm={async () => {
          if (optToggling) await toggleOptOut(optToggling);
        }}
      />
    </>
  );
}

/** Subscribed or opted out as a dot and a word, with a tick when consent is on file. Quieter than two badges per row. */
function StatusDot({ optedOut, consent }: { optedOut: boolean; consent: string | null }) {
  return (
    <span className="flex items-center gap-1.5 text-sm whitespace-nowrap">
      <span className={cn('size-2 shrink-0 rounded-full', optedOut ? 'bg-warning' : 'bg-success')} aria-hidden />
      {optedOut ? 'Opted out' : 'Subscribed'}
      {consent && (
        <span className="text-muted-foreground flex items-center" title={`Consent on file: ${consent}`}>
          <CheckIcon className="size-3.5" aria-hidden />
          <span className="sr-only">Consent on file</span>
        </span>
      )}
    </span>
  );
}

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
